<template>
    <div class="progress-container" v-show="loading">
        <el-progress 
            :percentage="percentage" 
            :show-text="false"
            :stroke-width="3"
            status="success"
            class="top-progress"/>
    </div>
    <router-view></router-view>
    <div 
        v-if="showFloatButton"
        id="custom-float-button" 
        class="custom-float-button"
        @click="handleFloatButtonClick">
        <i class="iconfont icon-kefu4"></i>
    </div>
    <el-backtop class="custom-backtop" style="bottom: 30px;right: 30px;"/>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getIpLocation } from '@/api/external/baiduMap';
import { ElNotification } from 'element-plus';
import { fetchCozeToken } from '@/api/auth';
import { STORAGE_KEYS, API_DOMAIN, COZE_CONFIG } from '@/constants';
export default {
    name: 'App',
    data(){
        return {
            loading: false,
            percentage: 0,
            timer: null,
        };
    },
    computed:{
        ...mapGetters('auth', ['getToken']),
        ...mapGetters('user', ['getUserId', 'getNickname', 'getAvatarUrl']),
        ...mapGetters(['getIp']),
        showFloatButton() {
            const hiddenPaths = ['/checkTicket', '/login', '/register'];
            return !hiddenPaths.includes(this.$route.path);
        }
    },
    methods:{
        ...mapActions(['updateIp']),
        ...mapActions('websocket', ['connect', 'disconnect']),
        setupCozeInterceptor(accessToken, botId) {
            const originalFetch = window.fetch;
            const cozeDomain = API_DOMAIN.COZE;
            const MAX_RETRY = COZE_CONFIG.MAX_RETRY;
            
            let currentAccessToken = accessToken;
            let currentBotId = botId;

            const fetchToken = async () => {
                 const response = await fetchCozeToken();
                 currentAccessToken = response.data?.accessToken || '';
                 currentBotId = response.data?.botId || '';
                 if (currentAccessToken) {
                     localStorage.setItem(STORAGE_KEYS.COZE_ACCESS_TOKEN, currentAccessToken);
                 }
                 if (currentBotId) {
                     localStorage.setItem(STORAGE_KEYS.COZE_BOT_ID, currentBotId);
                 }
                 return currentAccessToken;
             };

            window.refreshCozeToken = fetchToken;

            const retryRequest = async (input, init, retryCount = 0) => {
                if (retryCount >= MAX_RETRY) {
                    throw new Error('Token刷新失败，请重试');
                }

                const newToken = await fetchToken();
                const headers = new Headers(init?.headers || {});
                headers.set('Authorization', `Bearer ${newToken}`);

                return originalFetch(input, { ...init, headers });
            };

            window.fetch = async function(input, init) {
                const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;

                if (!url.startsWith(cozeDomain)) {
                    return originalFetch.apply(this, arguments);
                }

                const headers = new Headers(init?.headers || {});
                const cachedToken = localStorage.getItem(STORAGE_KEYS.COZE_ACCESS_TOKEN) || currentAccessToken;
                if (cachedToken) {
                    headers.set('Authorization', `Bearer ${cachedToken}`);
                }

                try {
                    const response = await originalFetch(url, { ...init, headers });
                    const contentType = response.headers.get('Content-Type') || '';
                    const isStream = contentType.includes('text/event-stream');

                    if (isStream) {
                        return response;
                    }

                    const clone = response.clone();
                    try {
                        const data = await clone.json();
                        if (response.status === 401 || data.code === 4100 || data.code === 4101) {
                            console.log('[Coze] Token失效，重试...');
                            return retryRequest(input, init, 0);
                        }
                    } catch (e) {}

                    return response;
                } catch (error) {
                    console.error('[Coze] 请求失败:', error);
                    throw error;
                }
            };
        },
        async handleFloatButtonClick() {
            if (!this.getToken) {
                ElNotification.warning({
                    title: '提示',
                    message: '未登录，登录后可使用智能客服',
                    position: 'bottom-right',
                    duration: 1500
                });
                return;
            }
            
            if (!window.cozeClient) {
                const loadSDK = () => {
                    return new Promise((resolve, reject) => {
                        if (window.CozeWebSDK && window.CozeWebSDK.WebChatClient) {
                            resolve();
                            return;
                        }
                        const script = document.createElement('script');
                        script.src = 'https://lf-cdn.coze.cn/obj/unpkg/flow-platform/chat-app-sdk/1.2.0-beta.20/libs/cn/index.js';
                        script.onload = resolve;
                        script.onerror = () => reject(new Error('SDK 加载失败'));
                        document.head.appendChild(script);
                    });
                };

                const loadConfig = () => {
                    return new Promise((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = '/coze-config.js';
                        script.onload = resolve;
                        script.onerror = () => reject(new Error('coze-config.js 加载失败'));
                        document.head.appendChild(script);
                    });
                };

                const initAndShow = async () => {
                    try {
                        let accessToken = localStorage.getItem(STORAGE_KEYS.COZE_ACCESS_TOKEN) || '';
                        let botId = localStorage.getItem(STORAGE_KEYS.COZE_BOT_ID) || '';
                        
                        if (!accessToken && this.getToken) {
                            try {
                                const response = await fetchCozeToken();
                                accessToken = response.data?.accessToken || '';
                                botId = response.data?.botId || '';
                                if (accessToken) {
                                    localStorage.setItem(STORAGE_KEYS.COZE_ACCESS_TOKEN, accessToken);
                                }
                                if (botId) {
                                    localStorage.setItem(STORAGE_KEYS.COZE_BOT_ID, botId);
                                }
                            } catch (error) {
                                console.error('获取Coze Token失败:', error);
                                ElNotification.error({
                                    title: '错误',
                                    message: '获取客服Token失败',
                                    position: 'bottom-right',
                                    duration: 2000
                                });
                                return;
                            }
                        }
                        
                        if (!accessToken) {
                            ElNotification.error({
                                title: '错误',
                                message: '获取客服访问令牌失败，请重试',
                                position: 'bottom-right',
                                duration: 2000
                            });
                            return;
                        }
                        
                        await loadSDK();
                        await loadConfig();
                        
                        this.setupCozeInterceptor(accessToken, botId);
                        
                        if (window.initCozeClient) {
                            window.initCozeClient(
                                {
                                    id: this.getUserId || '',
                                    nickname: this.getNickname || '用户',
                                    url: this.getAvatarUrl || ''
                                },
                                accessToken,
                                botId
                            );
                        }
                    } catch (error) {
                        console.error('客服初始化失败:', error);
                        ElNotification.error({
                            title: '错误',
                            message: '客服初始化失败',
                            position: 'bottom-right',
                            duration: 2000
                        });
                    }
                };

                await initAndShow();
            } else {
                if (window.cozeClient.showChatBot) {
                    window.cozeClient.showChatBot();
                }
            }
        },
        startProgress() {
            this.loading = true;
            this.percentage = 0;
            this.timer = setInterval(() => {
                if (this.percentage < 90) {
                    this.percentage += (90 - this.percentage) * 0.2;
                }
            }, 100);
        },
        finishProgress() {
            this.percentage = 100;
            setTimeout(() => {
                clearInterval(this.timer);
                this.loading = false;
                this.percentage = 0;
            }, 300);
        },
        async updateUserLocation() {
            this.updateIp('获取位置中...');
            try {
                const data = await getIpLocation();
                if (data.status) {
                    let location = '';
                    const province = data.province.replace('省', '');
                    const city = data.city.replace('市', '');
                    location = `${province} ${city}`;
                    this.updateIp(location);
                } else {
                    this.updateIp('无法获取位置');
                }
            } catch (error) {
                console.error('获取用户位置信息失败:', error);
                this.updateIp('无法获取位置');
            }
        }
    },
    async mounted() {
        await this.updateUserLocation();
        if (this.getToken) {
            await this.connect();
        }
        this.$router.beforeEach((to, from, next) => {
            this.startProgress();
            next();
        });
        // 路由跳转完成时结束进度条
        this.$router.afterEach(() => {
            this.finishProgress();
        });
    },
    beforeUnmount() {
        // 应用关闭时断开连接
        this.disconnect();
    }
}
</script>

<style scoped>
.progress-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 9999;
}

.top-progress {
    position: absolute;
    width: 100%;
}

:deep(.el-progress-bar__outer) {
    background-color: transparent !important;
}

:deep(.el-progress-bar__inner) {
    transition: all 0.2s linear;
    background-color: #1f628c !important;
}

.custom-backtop {
    background-color: #ffffff;
    width: 50px;
    height: 50px;
    border-radius: 10px;
    box-shadow: 0 2px 5px rgb(147, 147, 147);
}
</style>
